import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { catalogApi } from '@/api/endpoints/catalog';
import { isLastFreeView, quotaStore } from '@/access/quotaStore';
import { useFreeTier } from '@/access/useFreeTier';
import { track } from '@/analytics/tracker';
import { useAuth } from '@/auth/AuthProvider';

export type VideoOpenSource = 'grid' | 'canvas' | 'shared' | 'deep_link' | 'saved';

export interface OpenVideoRequest {
  id: number;
  source: VideoOpenSource;
  /** Позиция в выдаче поиска и id поиска — для search_result_clicked (CAT-15). */
  position?: number;
  searchEventId?: number | null;
  searchQuery?: string;
}

interface OpenMeta {
  source: VideoOpenSource;
  position?: number;
  searchEventId: number | null;
  searchQuery?: string;
  /** Панель открыта из приложения — запись истории наша, закрытие идёт «назад». */
  pushed: boolean;
  searchAtOpen: string;
}

interface VideoDetailContextValue {
  videoId: number | null;
  meta: OpenMeta | null;
  source: VideoOpenSource;
  /** Список для ‹ › (VID-4); страницы регистрируют свою выдачу. */
  listIds: number[];
  canPrev: boolean;
  canNext: boolean;
  openVideo: (request: OpenVideoRequest) => void;
  closeVideo: () => void;
  navigateVideo: (direction: 'prev' | 'next') => void;
  setNavigationList: (ids: number[]) => void;
  /**
   * Видео, открытие которого списало последний бесплатный просмотр дня (остаток >0 → 0).
   * Живёт, пока панель показывает это видео: листание, другое видео и закрытие его снимают.
   */
  lastFreeViewId: number | null;
}

const VideoDetailContext = createContext<VideoDetailContextValue | null>(null);
const PARAM = 'video';

/**
 * Панель разбора живёт в адресе: `?video=ID` (CAT-11, NAV-10). Открытие из
 * приложения пишет запись истории через роутер, закрытие — «назад»; прямая
 * ссылка (записи нет) чистится replace без лишней записи. Клик по результату
 * поиска фиксируется здесь (CAT-15).
 */
export function VideoDetailProvider({ children }: { children: ReactNode }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [meta, setMeta] = useState<OpenMeta | null>(null);
  const [listIds, setListIds] = useState<number[]>([]);
  const metaRef = useRef(meta);
  metaRef.current = meta;
  const { user } = useAuth();
  const userQuotaRef = useRef(user?.quota?.detail_views ?? null);
  userQuotaRef.current = user?.quota?.detail_views ?? null;
  const [lastFreeViewId, setLastFreeViewId] = useState<number | null>(null);
  const lastFreeViewRef = useRef(lastFreeViewId);
  lastFreeViewRef.current = lastFreeViewId;

  const raw = searchParams.get(PARAM);
  const videoId = raw && /^\d+$/.test(raw) ? Number(raw) : null;

  // Панель закрыта (роутер, «Назад», уход на другой раздел) — метаданные открытия больше не нужны.
  useEffect(() => {
    if (videoId === null && metaRef.current) setMeta(null);
  }, [videoId]);

  // Строка последнего просмотра привязана к одному видео: панель ушла с него — метки нет.
  useEffect(() => {
    if (lastFreeViewRef.current !== null && videoId !== lastFreeViewRef.current)
      setLastFreeViewId(null);
  }, [videoId]);

  const { guardDetailView } = useFreeTier();
  const openVideo = useCallback(
    (request: OpenVideoRequest) => {
      // Остаток до списания — из того же источника, что у счётчика: quotaStore, иначе /me.
      const quotaBefore = quotaStore.get() ?? userQuotaRef.current;
      const proceed = () => {
        if (request.searchEventId && request.position !== undefined) {
          catalogApi.trackSearchClick({
            search_event_id: request.searchEventId,
            video_reference_id: request.id,
            rank_overall: request.position + 1,
          });
          track('search_result_clicked', {
            metadata: {
              video_id: request.id,
              position: request.position + 1,
              search_query: request.searchQuery,
              search_event_id: request.searchEventId,
            },
          });
        }
        const alreadyOpen = videoId !== null;
        const next = new URLSearchParams(window.location.search);
        next.set(PARAM, String(request.id));
        setMeta({
          source: request.source,
          position: request.position,
          searchEventId: request.searchEventId ?? null,
          searchQuery: request.searchQuery,
          pushed: alreadyOpen ? (metaRef.current?.pushed ?? false) : true,
          searchAtOpen: alreadyOpen
            ? (metaRef.current?.searchAtOpen ?? `?${next.toString()}`)
            : `?${next.toString()}`,
        });
        // Списание этого открытия довело остаток до нуля — панель покажет строку с ценой.
        setLastFreeViewId(isLastFreeView(quotaBefore, quotaStore.get()) ? request.id : null);
        // Клик по другой карточке при открытой панели меняет содержимое без новой записи истории.
        setSearchParams(next, { replace: alreadyOpen });
      };
      // Бесплатный тариф: сначала списываем дневной просмотр; при исчерпании — окно оплаты вместо панели.
      void guardDetailView(request.id).then((allowed) => {
        if (allowed) proceed();
      });
    },
    [setSearchParams, videoId, guardDetailView],
  );

  const closeVideo = useCallback(() => {
    if (videoId === null) return;
    track('video_detail_closed', {
      metadata: { video_id: videoId, source: metaRef.current?.source ?? 'deep_link' },
    });
    const current = metaRef.current;
    const next = new URLSearchParams(window.location.search);
    next.delete(PARAM);
    const untouched = current?.pushed && window.location.search === current.searchAtOpen;
    setMeta(null);
    if (untouched) navigate(-1);
    else setSearchParams(next, { replace: true });
  }, [navigate, setSearchParams, videoId]);

  const index = videoId === null ? -1 : listIds.indexOf(videoId);
  const canPrev = index > 0;
  const canNext = index >= 0 && index < listIds.length - 1;

  const navigateVideo = useCallback(
    (direction: 'prev' | 'next') => {
      if (videoId === null) return;
      const nextIndex = direction === 'prev' ? index - 1 : index + 1;
      const nextId = listIds[nextIndex];
      if (nextId === undefined) return;
      track('video_detail_navigate', {
        metadata: { video_id: nextId, direction, source: metaRef.current?.source ?? 'deep_link' },
      });
      const next = new URLSearchParams(window.location.search);
      next.set(PARAM, String(nextId));
      setMeta((prev) =>
        prev
          ? {
              ...prev,
              position: prev.position === undefined ? undefined : nextIndex,
              searchAtOpen: `?${next.toString()}`,
            }
          : prev,
      );
      setSearchParams(next, { replace: true });
    },
    [index, listIds, setSearchParams, videoId],
  );

  const setNavigationList = useCallback((ids: number[]) => {
    setListIds((prev) =>
      prev.length === ids.length && prev.every((v, i) => v === ids[i]) ? prev : ids,
    );
  }, []);

  const value = useMemo<VideoDetailContextValue>(
    () => ({
      videoId,
      meta,
      source: meta?.source ?? 'deep_link',
      listIds,
      canPrev,
      canNext,
      openVideo,
      closeVideo,
      navigateVideo,
      setNavigationList,
      lastFreeViewId,
    }),
    [
      videoId,
      meta,
      listIds,
      canPrev,
      canNext,
      openVideo,
      closeVideo,
      navigateVideo,
      setNavigationList,
      lastFreeViewId,
    ],
  );

  // location в зависимостях не нужен, но панель должна перерисоваться при смене адреса.
  void location;

  return <VideoDetailContext.Provider value={value}>{children}</VideoDetailContext.Provider>;
}

export function useVideoDetail(): VideoDetailContextValue {
  const value = useContext(VideoDetailContext);
  if (!value) throw new Error('useVideoDetail must be used inside <VideoDetailProvider>');
  return value;
}
